/**
 * Round 12 #728 (T-006, mock v1.4 "Admin") — Admin's sectioned rail: a 260px column of the platform
 * declaration's Admin groups (GET /api/apps), under their sections in the declared order, each a card
 * with its subtitle; the group holding the current page is the raised card with the accent rail.
 * Nothing here names a group or a section: the declaration does.
 */
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMe } from '@/api/queries';
import { adminGroupFor, adminGroups, platformOf } from '@/app/platform';
import { RailItem } from '@/components/core';
import { type MenuDecl, useApps } from '@/features/apps/apps-api';
import { cn } from '@/lib/utils';

/** The groups split into consecutive sections, in the declaration's order. */
export function railSections(groups: readonly MenuDecl[]): { section: string; groups: MenuDecl[] }[] {
  const out: { section: string; groups: MenuDecl[] }[] = [];
  for (const g of groups) {
    const section = g.section ?? '';
    const last = out.at(-1);
    if (last && last.section === section) last.groups.push(g);
    else out.push({ section, groups: [g] });
  }
  return out;
}

export function AdminRailView({ groups, current }: { groups: readonly MenuDecl[]; current: string | null }) {
  // R-13 (axe "list"): each section is a heading and its own list, so every list child is a <li>
  return (
    <nav aria-label="Admin sections" data-testid="admin-rail">
      {railSections(groups).map((s) => (
        <div key={s.section || 'groups'}>
          {s.section ? (
            <p className="core-section-label m-0" id={`admin-rail-section-${s.section.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              {s.section}
            </p>
          ) : null}
          <ul className="m-0 p-0" aria-labelledby={s.section ? `admin-rail-section-${s.section.toLowerCase().replace(/[^a-z]+/g, "-")}` : undefined}>
            {s.groups.map((g) => (
              <RailItem key={g.id} to={g.items[0]!.route} title={g.label} sub={g.subtitle} selected={g.id === current} testId={`admin-rail-${g.id}`} />
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

/** The group holding the current page (the longest matching page route), for the rail and the page strip. */
export function useAdminGroups() {
  const me = useMe();
  const apps = useApps(Boolean(me.data));
  const groups = adminGroups(platformOf(apps.data).adminGroups, me.data?.roles.map((r) => r.role) ?? []);
  const { pathname } = useLocation();
  // a model tab (/admin/schema/types) belongs to the Model group through its page's route prefix
  const hit = adminGroupFor(groups, pathname);
  return { groups, hit, loading: apps.isLoading };
}

/** Pages of a group with more than one, as a tab strip above the page (the rail opens a group at its first page). */
export function GroupPages({ group, current, className }: { group: MenuDecl; current: string; className?: string }) {
  if (group.items.length < 2) return null;
  return (
    <nav aria-label={`${group.label} pages`} data-testid={`admin-group-pages-${group.id}`} className={cn('mb-4 flex flex-wrap gap-1', className)}>
      {group.items.map((it) => (
        <Link
          key={it.id}
          to={it.route}
          aria-current={it.id === current ? 'page' : undefined}
          data-testid={`admin-group-page-${it.id}`}
          className={cn(
            'rounded-md px-2.5 py-1.5 text-[length:var(--t-control)] font-semibold no-underline',
            it.id === current ? 'bg-surface-2 text-foreground' : 'text-muted-foreground hover:bg-hover hover:text-foreground',
          )}
        >
          {it.label}
        </Link>
      ))}
    </nav>
  );
}

/**
 * Every Admin page in the rail's frame: the rail at the left (260px), the page at the right with its
 * group's other pages as a strip. A role that sees no Admin group gets the page alone (it explains
 * the refusal); nothing is disabled.
 *
 * R-14: the page sits at the same place in the tree whether or not the groups have loaded. It used to
 * be returned bare until GET /api/apps answered and then re-parented into the grid, which remounted
 * it — under load a tab chosen or a form opened in that window was lost (the page's state reset).
 */
export function AdminFrame({ children }: { children: ReactNode }) {
  const { groups, hit } = useAdminGroups();
  const framed = groups.length > 0;
  return (
    <div
      className={framed ? 'grid w-full gap-[22px] md:grid-cols-[260px_minmax(0,1fr)] [&_.page-body]:max-w-none [&_.page-body]:p-0' : 'contents'}
      data-testid={framed ? 'admin-frame' : undefined}
    >
      {framed ? <AdminRailView groups={groups} current={hit?.group.id ?? null} /> : null}
      <div className={framed ? 'min-w-0' : 'contents'}>
        {hit ? <GroupPages group={hit.group} current={hit.item.id} /> : null}
        {children}
      </div>
    </div>
  );
}
